// «Por conductores» del informe Comportamiento del conductor (en Mapon,
// pestaña Por conductores). Puro, con test.
//
// Cada unidad se le atribuye al conductor que tiene asignado HOY (devices →
// driver, el mismo de la tarjeta del mapa): v5 no guarda el historial de
// asignaciones, así que una unidad que cambió de conductor en el periodo cuenta
// toda para el actual.
// shortcut: asignación actual, no histórica; cuando exista el historial de
// asignaciones (driver_assignments), repartir los puntos por tramo.
import { PARAM_KEYS, type DrivingScore, type ParamKey } from "./drivingScore";

export interface DriverRow {
  id: string;
  driverName: string;
  vehicles: string[];
  overall: number | null;
  params: Record<ParamKey, { score: number | null; value: number }>;
  distanceKm: number;
  drivingMs: number;
}

const COUNT_KEYS = new Set<ParamKey>(["harshBrake", "harshAccel", "harshTurn"]);
export const NO_DRIVER = "__sin_conductor__";

/**
 * Calificación del conductor = promedio de las de sus unidades PONDERADO por
 * tiempo de manejo (una unidad que manejó 8 h pesa más que una de 10 min). Los
 * bruscos se suman; los porcentajes (eco, ralentí, excesos) se ponderan igual.
 */
export const aggregateByDriver = (
  results: { deviceId: string; vehicleName: string; score: DrivingScore }[],
  driverOf: (deviceId: string) => { id?: string; name?: string } | null | undefined,
): DriverRow[] => {
  const groups = new Map<string, { name: string; items: typeof results }>();
  for (const r of results) {
    const d = driverOf(r.deviceId);
    const id = d?.id || NO_DRIVER;
    const g = groups.get(id) ?? { name: d?.id ? d.name || "Conductor sin nombre" : "Sin conductor asignado", items: [] };
    g.items.push(r);
    groups.set(id, g);
  }

  const weighted = (items: typeof results, pick: (s: DrivingScore) => number | null) => {
    let w = 0;
    let acc = 0;
    for (const { score } of items) {
      const v = pick(score);
      if (v == null || score.drivingMs <= 0) continue;
      w += score.drivingMs;
      acc += v * score.drivingMs;
    }
    return w > 0 ? acc / w : null;
  };

  return [...groups.entries()]
    .map(([id, g]) => {
      const params = Object.fromEntries(
        PARAM_KEYS.map((k) => [
          k,
          {
            score: weighted(g.items, (s) => s.params[k].score),
            value: COUNT_KEYS.has(k)
              ? g.items.reduce((n, { score }) => n + score.params[k].value, 0)
              : weighted(g.items, (s) => s.params[k].value) ?? 0,
          },
        ]),
      ) as DriverRow["params"];
      return {
        id,
        driverName: g.name,
        vehicles: g.items.map((i) => i.vehicleName),
        overall: weighted(g.items, (s) => s.overall),
        params,
        distanceKm: g.items.reduce((n, { score }) => n + score.distanceKm, 0),
        drivingMs: g.items.reduce((n, { score }) => n + score.drivingMs, 0),
      };
    })
    .sort((a, b) => (a.id === NO_DRIVER ? 1 : b.id === NO_DRIVER ? -1 : (b.overall ?? -1) - (a.overall ?? -1)));
};
